import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ReceiptLong as ReceiptIcon,
  CheckCircle as CheckIcon,
  Help as HelpIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const ClientAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/clients/${id}/account_statement/`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching client account:', err);
      setError('No se pudo cargar la cuenta corriente del cliente.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprobado':
        return { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', icon: <CheckIcon fontSize="small" /> };
      case 'revision':
        return { bg: '#fff3e0', color: '#ff9800', border: '#ffe0b2', icon: <HelpIcon fontSize="small" /> };
      case 'bloqueado':
        return { bg: '#ffebee', color: '#d32f2f', border: '#ffcdd2', icon: <BlockIcon fontSize="small" /> };
      default:
        return { bg: '#f5f5f5', color: '#9e9e9e', border: '#e0e0e0', icon: null };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aprobado':
        return 'Aprobado';
      case 'revision':
        return 'Revisión';
      case 'bloqueado':
        return 'Bloqueado';
      default:
        return status;
    }
  };

  const getPriceListLabel = (priceList) => {
    const labels = {
      mayorista_a: 'Mayorista A',
      mayorista_b: 'Mayorista B',
      premium: 'Premium',
      estandar: 'Estándar',
    };
    return labels[priceList] || priceList;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress color="primary" />
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="error" variant="h6" sx={{ mb: 2 }}>{error || 'Cliente no encontrado'}</Typography>
          <Button variant="contained" onClick={() => navigate('/clients')} startIcon={<ArrowBackIcon />}>
            Volver a Clientes
          </Button>
        </Box>
      </Box>
    );
  }

  const { client, statement } = data;
  const statusConfig = getStatusColor(client.credit_status);
  const availableCredit = Math.max(0, parseFloat(client.credit_limit || 0) - parseFloat(client.current_balance || 0));

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer', color: '#4e7397', fontSize: '0.875rem' }}>
            Inicio
          </Link>
          <Link color="inherit" onClick={() => navigate('/clients')} sx={{ cursor: 'pointer', color: '#4e7397', fontSize: '0.875rem' }}>
            Clientes
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Cuenta Corriente
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/clients')}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderColor: '#d0dbe7',
              color: '#4e7397',
              bgcolor: 'white',
              '&:hover': { borderColor: '#800020', color: '#800020', bgcolor: 'rgba(128,0,32,0.04)' },
              borderRadius: '0.5rem',
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Volver
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 900, color: '#0e141b', letterSpacing: '-0.03em' }}>
            Cuenta Corriente
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Client summary card */}
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                p: 3,
                borderRadius: '1rem',
                border: '1px solid #d0dbe7',
                boxShadow: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#800020', width: 56, height: 56, fontSize: '1.25rem', fontWeight: 900 }}>
                  {getInitials(client.name)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0e141b', lineHeight: 1.2 }}>
                    {client.name}
                  </Typography>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', mt: 0.5 }}>
                    CUIT: {client.cuit}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#eef2f6' }} />

              {/* Contact and General Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Cód. Tango:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.875rem' }}>{client.cod_cliente || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Contacto:</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' }}>{client.contact_person || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Email:</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' }}>{client.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Teléfono:</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' }}>{client.phone || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Dirección:</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem', textAlign: 'right' }}>
                    {client.delivery_address} ({client.city})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Canales:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {client.is_retail && <Chip label="Minorista" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />}
                    {client.is_wholesale && <Chip label="Mayorista" size="small" sx={{ bgcolor: '#fff3e0', color: '#f57c00', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#eef2f6' }} />

              {/* Credit Status & Limits */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: '#4e7397', fontWeight: 600 }}>Estado Crédito:</Typography>
                  <Chip
                    label={getStatusLabel(client.credit_status)}
                    size="small"
                    sx={{
                      bgcolor: statusConfig.bg,
                      color: statusConfig.color,
                      border: `1px solid ${statusConfig.border}`,
                      fontWeight: 700,
                    }}
                    icon={statusConfig.icon}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: '#4e7397', fontWeight: 600 }}>Lista Precios:</Typography>
                  <Chip label={getPriceListLabel(client.price_list)} size="small" sx={{ fontWeight: 700 }} />
                </Box>

                <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: '0.75rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>Límite de Crédito:</Typography>
                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                      ${parseFloat(client.credit_limit || 0).toLocaleString('es-AR')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>Saldo Actual:</Typography>
                    <Typography sx={{ fontWeight: 800, color: parseFloat(client.current_balance || 0) > 0 ? '#d32f2f' : '#2e7d32' }}>
                      ${parseFloat(client.current_balance || 0).toLocaleString('es-AR')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.9rem' }}>Crédito Disponible:</Typography>
                    <Typography sx={{ fontWeight: 900, color: availableCredit > 0 ? '#2e7d32' : '#d32f2f', fontSize: '1.05rem' }}>
                      ${availableCredit.toLocaleString('es-AR')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Statement movements list */}
          <Grid item xs={12} lg={8}>
            <Paper
              sx={{
                p: 3,
                borderRadius: '1rem',
                border: '1px solid #d0dbe7',
                boxShadow: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ReceiptIcon sx={{ color: '#800020' }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0e141b' }}>
                  Movimientos de Cuenta Corriente
                </Typography>
              </Box>

              <TableContainer sx={{ flex: 1, maxHeight: '60vh', display: { xs: 'none', md: 'block' } }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'white', color: '#4e7397', fontWeight: 600 }}>Fecha</TableCell>
                      <TableCell sx={{ bgcolor: 'white', color: '#4e7397', fontWeight: 600 }}>Detalle / Concepto</TableCell>
                      <TableCell sx={{ bgcolor: 'white', color: '#4e7397', fontWeight: 600 }} align="right">Debe</TableCell>
                      <TableCell sx={{ bgcolor: 'white', color: '#4e7397', fontWeight: 600 }} align="right">Haber</TableCell>
                      <TableCell sx={{ bgcolor: 'white', color: '#4e7397', fontWeight: 600 }} align="right">Saldo Acum.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!statement || statement.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#64748b' }}>
                          No hay movimientos registrados en la cuenta corriente.
                        </TableCell>
                      </TableRow>
                    ) : (
                      statement.map((item, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{item.date}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.description}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }} align="right">
                            {item.debit ? `$${parseFloat(item.debit).toLocaleString('es-AR')}` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }} align="right">
                            {item.credit ? `$${parseFloat(item.credit).toLocaleString('es-AR')}` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 700 }} align="right">
                            ${parseFloat(item.balance).toLocaleString('es-AR')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile: Tarjetas de movimientos */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, maxHeight: '60vh', overflowY: 'auto' }}>
                {!statement || statement.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ color: '#64748b' }}>No hay movimientos registrados en la cuenta corriente.</Typography>
                  </Box>
                ) : (
                  statement.map((item, idx) => (
                    <Box key={idx} sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0e141b', minWidth: 0 }}>{item.description}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', flexShrink: 0 }}>{item.date}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Debe</Typography>
                          <Typography sx={{ fontSize: '0.875rem', color: '#d32f2f' }}>{item.debit ? `$${parseFloat(item.debit).toLocaleString('es-AR')}` : '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Haber</Typography>
                          <Typography sx={{ fontSize: '0.875rem', color: '#2e7d32' }}>{item.credit ? `$${parseFloat(item.credit).toLocaleString('es-AR')}` : '-'}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Saldo</Typography>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 800 }}>${parseFloat(item.balance).toLocaleString('es-AR')}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientAccount;
